import React, { Fragment, useState } from 'react';
import { useRecordContext } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { capitalize } from 'inflection';
import LinkField from 'components/LinkField';
import { formatDateTime } from 'components/DateTimeField';
import { DisplayHelpers } from 'utils/helpers';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import isNil from 'lodash/isNil';
import get from 'lodash/get';

export type FieldConfig = {
  key: string;
  label: string;
  source: string;
  formatter: (value: string | number) => string | null;
  path?: string;
  customGetter?: (record: any) => number | null;
};

type Audit = {
  id: string | number;
  action: string;
  account?: { name: string } | null;
  audited_changes: Record<string, unknown>;
  created_at: string;
  user_id: string | number;
};

const ten99 = [
  {
    key: 'base_charge_rate_cents',
    label: 'ten99.base_charge_rate_cents',
    path: 'ten99.base_charge_rate_cents',
    formatter: DisplayHelpers.money,
    source: 'settings',
  },
  {
    key: 'base_margin_percent',
    label: 'ten99.base_margin_percent',
    path: 'ten99.base_margin_percent',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'invoicing_markup',
    label: 'ten99.invoicing_markup',
    path: 'ten99.invoicing_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'misc_markup',
    label: 'ten99.misc_markup',
    path: 'ten99.misc_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'ten99_blended_rate_markup',
    label: 'ten99.blended_rate_markup',
    customGetter: (record) => {
      const helper = record && new RateSettingsDisplayHelper({ settings: record });
      return helper?.getBlendedRateMarkup('ten99');
    },
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'ten99_use_blended_rate',
    label: 'ten99.use_blended_rate',
    path: 'ten99.use_blended_rate',
    formatter: (value) => (isNil(value) ? null : String(value)),
    source: 'settings',
  },
];

const w2Local = [
  {
    key: 'base_charge_rate_cents_w2_local',
    label: 'w2.local.base_charge_rate_cents',
    path: 'w2.local.base_charge_rate_cents',
    formatter: DisplayHelpers.money,
    source: 'settings',
  },
  {
    key: 'base_margin_percent_w2',
    label: 'base_margin_percent_w2',
    path: 'w2.local.base_margin_percent_w2',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_local_invoicing_markup',
    label: 'w2.local.invoicing_markup',
    path: 'w2.local.invoicing_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_local_misc_markup',
    label: 'w2.local.misc_markup',
    path: 'w2.local.misc_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_local_operational_markup',
    label: 'w2.local.operational_markup',
    path: 'w2.local.operational_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_blended_rate_markup',
    label: 'w2_per_diem.blended_rate_markup',
    customGetter: (record: any) => {
      const helper = record && new RateSettingsDisplayHelper({ settings: record });
      return helper?.getBlendedRateMarkup('w2.local');
    },
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_use_blended_rate',
    label: 'w2.local.use_blended_rate',
    path: 'w2.local.use_blended_rate',
    formatter: (value: any) => (isNil(value) ? null : String(value)),
    source: 'settings',
  },
];

const w2Travel = [
  {
    key: 'base_charge_rate_cents_w2_travel',
    label: 'w2.travel.base_charge_rate_cents',
    path: 'w2.travel.base_charge_rate_cents',
    formatter: DisplayHelpers.money,
    source: 'settings',
  },
  {
    key: 'base_margin_percent_w2_tcv',
    label: 'w2.travel.base_margin_percent_w2_tcv',
    path: 'w2.travel.base_margin_percent_w2_tcv',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'base_margin_percent_w2_travel',
    label: 'w2.travel.base_margin_percent_w2_travel',
    path: 'w2.travel.base_margin_percent_w2_travel',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_travel_invoicing_markup',
    label: 'w2.travel.invoicing_markup',
    path: 'w2.travel.invoicing_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_travel_misc_markup',
    label: 'w2.travel.misc_markup',
    path: 'w2.travel.misc_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'w2_travel_operational_markup',
    label: 'w2.travel.operational_markup',
    path: 'w2.travel.operational_markup',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
];

const guardrails = [
  {
    key: 'base_margin_percent_1099_guardrail',
    label: 'base_margin_percent_1099_guardrail',
    path: 'ten99.base_margin_percent_1099_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'base_margin_percent_w2_guardrail',
    label: 'base_margin_percent_w2_guardrail',
    path: 'w2.base_margin_percent_w2_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'payout_rate_percent_1099_guardrail',
    label: 'payout_rate_percent_1099_guardrail',
    path: 'ten99.payout_rate_percent_1099_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'payout_rate_percent_w2_guardrail',
    label: 'payout_rate_percent_w2_guardrail',
    path: 'w2.base_margin_percent_w2_guardrail',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'incremental_margin_percent_1099',
    label: 'incremental_margin_percent_1099',
    path: 'ten99.incremental_margin_percent_1099',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
  {
    key: 'incremental_margin_percent_w2',
    label: 'incremental_margin_percent_w2',
    path: 'w2.incremental_margin_percent_w2',
    formatter: DisplayHelpers.percentage,
    source: 'settings',
  },
];

export const FIELD_CONFIGS: FieldConfig[] = [...ten99, ...w2Local, ...w2Travel, ...guardrails];

function renderAuditRow(config: FieldConfig, audit: Audit) {
  const { action, account, audited_changes, created_at, user_id } = audit;
  const isUpdate = action === 'update';
  let originalValue, updatedValue, hasChanged;

  if (config.source === 'settings') {
    const settingsValue = audited_changes.settings;
    const [originalRecord, updatedRecord] = isUpdate
      ? Array.isArray(settingsValue)
        ? settingsValue
        : [settingsValue, settingsValue]
      : [Array.isArray(settingsValue) ? settingsValue[0] : settingsValue];

    if (config.customGetter) {
      originalValue = config.customGetter(originalRecord);
      updatedValue = config.customGetter(updatedRecord);
    } else {
      originalValue = get(originalRecord, config.path);
      updatedValue = get(updatedRecord, config.path);
    }

    const bothNil = isNil(originalValue) && isNil(updatedValue);
    hasChanged = !bothNil && (!isUpdate || `${updatedValue}` !== `${originalValue}`);
  } else {
    hasChanged = audited_changes[config.key] !== undefined;
    const columnValue = audited_changes[config.key];
    [originalValue, updatedValue] = isUpdate
      ? (Array.isArray(columnValue) ? columnValue : [null, columnValue]) || [null, null]
      : [null, Array.isArray(columnValue) ? columnValue[0] : columnValue];
  }

  if (!hasChanged) {
    return null;
  }

  const formattedOriginal =
    originalValue !== null && originalValue !== undefined ? config.formatter(originalValue) : null;
  const formattedUpdated =
    updatedValue !== null && updatedValue !== undefined ? config.formatter(updatedValue) : null;

  return (
    <TableRow key={`${audit.id}-${config.key}`}>
      <TableCell>{capitalize(action)}</TableCell>
      <TableCell>
        {formatDateTime({ timeZone: 'America/Los_Angeles' })('MM/dd/yyyy HH:mm zzz')(created_at)}
      </TableCell>
      <TableCell>
        {!!account && (
          <LinkField to={`/Account/${user_id}/show`} href="_blank">
            {account.name}
          </LinkField>
        )}
      </TableCell>
      <TableCell>{config.label}</TableCell>
      <TableCell title={originalValue}>{formattedOriginal}</TableCell>
      <TableCell title={updatedValue}>{formattedUpdated}</TableCell>
    </TableRow>
  );
}

const RateAudits = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  return !!record.audits ? (
    <>
      <Button variant="outlined" color="primary" onClick={() => setOpen(true)}>
        View Rates Change Log
      </Button>

      <Dialog fullWidth maxWidth="lg" open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Rates Change Log</DialogTitle>
        <DialogContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Action</TableCell>
                <TableCell>Updated At</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Original</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...record.audits]
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((audit) => (
                  <Fragment key={`audit-table-row-${audit.id}`}>
                    {FIELD_CONFIGS.map((config) => renderAuditRow(config, audit))}
                  </Fragment>
                ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  ) : null;
};

export default RateAudits;
